<script setup lang="ts">
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'
import {computed} from 'vue'

type ButtonVariantWithoutCustom =
  | 'primary'
  | 'secondary'
  | 'light'
  | 'success'
  | 'warning'
  | 'warning-text'
  | 'new-primary'
  | 'new-secondary'
  | 'new-neutral'
  | 'new-attention'
  | 'new-confirm'
type ButtonVariant = ButtonVariantWithoutCustom | 'custom'

interface Props {
  customVariant?: string
  id?: string
  label?: string
  variant?: ButtonVariant
  isLoading?: boolean
  isDisabled?: boolean
  prependIcon?: string
  type?: HTMLButtonElement['type']
  size?: 'small' | 'medium' | 'large'
  /** @deprecated do not use for new UI */
  slim?: boolean
  iconOnly?: boolean
}

const isNewUiEnabled = useNewUiEnabled()

const props = withDefaults(defineProps<Props>(), {
  customVariant: '',
  type: 'button',
  slim: false,
  size: 'medium',
})

const variant = computed(() => props.variant ?? (isNewUiEnabled.value ? 'new-primary' : 'primary'))
</script>

<template>
  <button
    :id="id"
    :class="[
      'w-fit uppercase tracking-widest transition-all relative',
      {
        'text-white': variant !== 'new-confirm',
        'text-verydarkgrey': variant === 'new-confirm',
        'rounded-lg inline-flex items-center justify-center leading-none': isNewUiEnabled,
        'focus-within:outline focus-within:outline-1 focus-within:outline-offset-2 focus-within:outline-new-primary-500':
          isNewUiEnabled && !props.isDisabled && !props.isLoading,
        'py-2 px-5': !isNewUiEnabled,
        '!w-6 !px-0': isNewUiEnabled && iconOnly && props.size === 'small',
        '!w-8 !px-0': isNewUiEnabled && iconOnly && props.size === 'medium',
        '!w-9 !px-0': isNewUiEnabled && iconOnly && props.size === 'large',
        'opacity-25': !isNewUiEnabled && props.isDisabled && !props.isLoading,
        'opacity-50': !isNewUiEnabled && props.isDisabled && props.isLoading,
        'pointer-events-none': props.isDisabled,
        'text-base px-3': props.slim,
        'h-9 px-new-2xl text-new-base': isNewUiEnabled && props.size === 'large',
        'h-8 px-new-xl text-new-sm': isNewUiEnabled && props.size === 'medium',
        'h-6 px-2.5 py-new-sm text-new-sm': isNewUiEnabled && props.size === 'small',
        [props.customVariant]: variant === 'custom',
      },
      {
        'bg-branddark hover:bg-branddarkdarker': variant === 'primary',
        'bg-brandmain hover:bg-rbc-tertiary': variant === 'secondary',
        'bg-brandlight hover:bg-brandlightdarker': variant === 'light',
        'bg-olive hover:bg-darkolive': variant === 'success',
        'bg-red hover:bg-darkred': variant === 'warning',
        '!text-red bg-lightgrey hover:bg-darkgrey': variant === 'warning-text',
      },
      {
        'bg-new-primary-default hover:bg-new-primary-hover': variant === 'new-primary',
        'bg-new-primary-disabled': variant === 'new-primary' && props.isDisabled,
        'bg-new-secondary-default hover:bg-new-secondary-hover': variant === 'new-secondary',
        'bg-new-tertiary-500': variant === 'new-secondary' && props.isDisabled,
        'bg-new-neutral-600 hover:bg-new-neutral-800': variant === 'new-neutral',
        'bg-new-neutral-300': variant === 'new-neutral' && props.isDisabled,
        'bg-new-red-500 hover:bg-new-red-600': variant === 'new-attention',
        'bg-new-red-300': variant === 'new-attention' && props.isDisabled,
        'bg-new-green-500 hover:bg-new-green-600': variant === 'new-confirm',
        'bg-new-green-300': variant === 'new-confirm' && props.isDisabled,
      },
    ]"
    :disabled="isDisabled || isLoading"
    :type="type"
  >
    <span v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
      <i class="fa-solid fa-circle-notch animate-spin leading-none tracking-normal"></i>
    </span>
    <span :style="{visibility: isLoading ? 'hidden' : undefined}">
      <i v-if="prependIcon" :class="[prependIcon, 'mr-1.5']" />
      <slot />
      <span>{{ label }}</span>
      <slot name="append" />
    </span>
  </button>
</template>
